import React, { useRef, useMemo } from "react";
import { PolarArea } from "react-chartjs-2";
import { Chart, RadialLinearScale, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import html2canvas from "html2canvas";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";

Chart.register(RadialLinearScale, ArcElement, Tooltip, Legend, ChartDataLabels);

const fallbackColors = [
    "#aeea84", "#fcd39e", "#a8a1e9", "#ff6384", "#36a2eb", "#ffce56", "#b8e994",
    "#f6e58d", "#686de0", "#303952", "#f5cd79", "#574b90", "#f3a683", "#574b90"
];

const truncateLabel = (str, maxLen = 32) => {
    if (typeof str !== "string") return "";
    return str.length > maxLen ? str.slice(0, maxLen - 1) + "…" : str;
};

const whiteBackgroundPlugin = {
    id: 'custom_canvas_background_color',
    beforeDraw: (chart) => {
        const { ctx } = chart;
        ctx.save();
        ctx.globalCompositeOperation = 'destination-over';
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, chart.width, chart.height);
        ctx.restore();
    }
};

function downloadAllChartsAsPng(containerRef, title = "all_charts") {
    if (!containerRef.current) return;
    html2canvas(containerRef.current, {
        backgroundColor: "#fff",
        scale: 4,            // Increase scale (try 3 or 4 for even sharper)
        useCORS: true,       // If needed for fonts or images
        allowTaint: true     // If needed for fonts or images
    }).then(canvas => {
        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = `${title.replace(/\s+/g, "_")}.png`;
        link.click();
    });
}

export default function PolarAreaChartsPerInterview({
    codes = [],
    files = [],
    titlePrefix = "Interview",
    title = ""
}) {
    const containerRef = useRef();

    // Build stable color map by code id
    const codeColorMap = useMemo(() => {
        const map = {};
        codes.forEach((code, idx) => {
            map[code.id] = code.color || fallbackColors[idx % fallbackColors.length];
        });
        return map;
    }, [codes]);

    // Filter out files that have no excerpts
    const filesWithExcerpts = useMemo(() => {
        return files.filter(file => {
            // Check if any code has excerpts for this file
            return codes.some(code =>
                Array.isArray(code.excerpts) &&
                code.excerpts.some(excerpt => excerpt.analysis_parent_id === file.id)
            );
        });
    }, [files, codes]);

    // Prepare legend data using the map
    const legendData = useMemo(() => {
        return codes
            .map(code => ({
                name: truncateLabel(code.name, 38),
                color: codeColorMap[code.id]
            }))
            .filter((entry, idx, arr) =>
                arr.findIndex(e => e.name === entry.name) === idx
            );
    }, [codes, codeColorMap]);

    // Show message if no interviews have excerpts
    if (filesWithExcerpts.length === 0) {
        return (
            <div className="flex justify-center items-center min-h-fit bg-white">
                <div className="w-full max-w-7xl">
                    <div className="border border-black rounded-lg shadow-lg p-8 bg-white w-full">
                        <div className="text-xl font-semibold mb-4">{title}</div>
                        <div className="text-gray-500 italic text-center py-8">
                            No interviews contain excerpts to display.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex justify-center items-center min-h-fit bg-white">
            <div className="w-full max-w-7xl">

                <div
                    className="border border-black rounded-lg shadow-lg p-8 bg-white w-full"
                >
                    {/* Chart grid */}
                    <div className="flex justify-between w-full mb-4" >

                        <div className="text-xl font-semibold whitespace-nowrap ">
                            {title}
                        </div>

                        <button
                            onClick={() => downloadAllChartsAsPng(containerRef, titlePrefix)}
                            className="flex items-center gap-2 px-3 py-1 bg-black text-white rounded hover:bg-gray-800"
                        >
                            <ArrowDownTrayIcon className="w-5 h-5" /> PNG
                        </button>
                    </div>
                    <div ref={containerRef}>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 justify-items-center items-center">
                            {filesWithExcerpts.map((file, i) => (
                                <SingleInterviewPolarAreaChart
                                    key={file.id || file.name || i}
                                    file={file}
                                    codes={codes}
                                    codeColorMap={codeColorMap}
                                    title={`${file.name || file.id || `Interview ${i + 1}`}`}
                                />
                            ))}
                        </div>
                        {/* Unified legend below charts */}
                        <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 mt-10 w-full">
                            {legendData.map((item, idx) => (
                                <svg
                                    key={idx}
                                    width="250"
                                    height="24"
                                    style={{ display: "block", marginBottom: 8 }}
                                >
                                    <rect x="0" y="3" width="18" height="18" fill={item.color} stroke="#bbb" rx="4" />
                                    <text
                                        x="26"
                                        y="14"
                                        fontFamily="Inter, Arial, Helvetica, sans-serif"
                                        fontSize="16"
                                        fill="#444"
                                        alignmentBaseline="middle"
                                    >
                                        {item.name}
                                    </text>
                                </svg>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SingleInterviewPolarAreaChart({ codes, file, title, codeColorMap }) {
    const chartRef = useRef();

    // Only codes with excerpts in THIS interview (file)
    const { labels, values, colors } = useMemo(() => {
        const filtered = codes.map((code) => {
            const count = Array.isArray(code.excerpts)
                ? code.excerpts.filter(
                    (ex) => ex.analysis_parent_id === file.id
                ).length
                : 0;
            return count > 0
                ? {
                    label: truncateLabel(code.name, 38),
                    value: count,
                    color: codeColorMap[code.id]
                }
                : null;
        }).filter(Boolean);

        return {
            labels: filtered.map(c => c.label),
            values: filtered.map(c => c.value),
            colors: filtered.map(c => c.color),
        };
    }, [codes, file, codeColorMap]);

    const data = {
        labels,
        datasets: [{
            label: "",
            data: values,
            backgroundColor: colors
        }]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1, // Always square
        layout: {
            padding: {
                top: 60,
                bottom: 60,
                left: 50,
                right: 50
            }
        },
        scales: {
            r: {
                pointLabels: {
                    display: false,
                    centerPointLabels: true,
                    font: { size: 12 },
                    padding: 10,
                },
                ticks: { display: false },
                beginAtZero: true
            }
        },
        devicePixelRatio: 3,
        plugins: {
            legend: { display: false }, // Hide Chart.js legend
            title: { display: false },
            datalabels: {
                display: true,
                color: "#000",
                font: { weight: "thin", size: 14 },
                formatter: (v) => (v > 0 ? v : ""),
                anchor: "middle",
                align: "middle",
                offset: 0,
            }
        }
    };

    return (
        <div className="flex flex-col items-center w-full max-w-[420px] mx-auto min-h-[450px]">
            <div className="text-xl font-semibold mb-4 text-center w-full">{title}</div>
            <PolarArea
                ref={chartRef}
                data={data}
                options={options}
                plugins={[whiteBackgroundPlugin]}
            />
        </div>
    );
}
